import { useEffect, useState } from "react";
import { breakpoints } from "@ux/tokens";

export interface Viewport {
    readonly width: number;
    readonly isPhone: boolean;
    readonly isTablet: boolean;
    readonly isDesktop: boolean;
}

/**
 * Subscribes a component to viewport-width changes and returns the
 * three responsive buckets the design uses. The hook is the single
 * source of truth for "should this render the phone variant?" — never
 * inline `window.innerWidth` checks in components, always go through
 * this hook so the breakpoints stay coherent.
 *
 * SSR is not in scope (this is a pure client SPA), so the initial read
 * is synchronous against `window.innerWidth`. The fallback for the
 * `typeof window === "undefined"` path is the desktop bucket — we'd
 * rather render the richer layout if anything ever runs us off-DOM
 * than collapse into a phone view by default.
 */
export function useViewport(): Viewport {
    const [width, setWidth] = useState<number>(() =>
        typeof window !== "undefined" ? window.innerWidth : breakpoints.tablet,
    );

    useEffect(() => {
        function read(): void {
            setWidth(window.innerWidth);
        }
        window.addEventListener("resize", read);
        // visualViewport fires on iOS Safari address-bar collapse, which
        // is a real width change for our purposes (the layout viewport
        // can shift). Belt-and-braces with the `resize` listener.
        window.visualViewport?.addEventListener("resize", read);
        return () => {
            window.removeEventListener("resize", read);
            window.visualViewport?.removeEventListener("resize", read);
        };
    }, []);

    return {
        width,
        isPhone: width < breakpoints.phone,
        isTablet: width >= breakpoints.phone && width < breakpoints.tablet,
        isDesktop: width >= breakpoints.tablet,
    };
}
